import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Alert, Modal, Spin, message } from "antd";
import {
  deleteDocExp,
  docExpCancel,
  docExpCheckInMany,
  docExpCheckOutMany,
  docExpClearPlacement,
  docExpConfirm,
  docExpRecalculate,
  docExpUncheckIn,
  docExpUncheckOut,
  docExpUnconfirm,
  fetchDocExpList,
  fetchDocExpLookups,
  fetchDocExpRecalculateCheck
} from "./doc-exp-api";
import DocExp from "./doc-exp";
import DocExpBookModal from "./doc-exp-book-modal";
import DocExpChangeNumberModal from "./doc-exp-change-number-modal";
import DocExpLifecycleModal from "./doc-exp-lifecycle-modal";
import DocExpMoveModal from "./doc-exp-move-modal";
import DocExpSellBookModal from "./doc-exp-sell-book-modal";
import DocExpsFilters from "./doc-exps-filters";
import DocExpsRowCard from "./doc-exps-row-card";
import {
  STATUS,
  filtersFromSearchParams,
  filtersSearchEquals,
  filtersSearchString,
  filtersToSearchParams,
  getLawFlag,
  loadStoredFilters,
  normalizeListRow,
  normalizeLookups,
  pick,
  saveFilters,
  toNumber
} from "./doc-exp-utils";
import "./doc-exps.css";

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 350;
const SCROLL_LOAD_GAP = 80;

const normalizePage = (data) => {
  const items = Array.isArray(data)
    ? data
    : Array.isArray(pick(data, "items", "Items"))
      ? pick(data, "items", "Items")
      : [];
  const hasMore = Array.isArray(data)
    ? items.length >= PAGE_SIZE
    : Boolean(pick(data, "hasMore", "HasMore"));
  return {
    items: items.map(normalizeListRow).filter((r) => r.idDoc != null),
    hasMore
  };
};

const isCheckInEligible = (r) =>
  r && !r.isClosed && (r.idStatus === STATUS.NEW || r.idStatus === STATUS.CONFIRMED);
const isCheckOutEligible = (r) => r && !r.isClosed && r.idStatus === STATUS.CHECKED_IN;

/**
 * Журнал путёвок. Delphi: TFrmJurnExp.
 */
const DocExps = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { id: routeId } = useParams();
  const isNew = routeId === "new";
  const editId = routeId && /^\d+$/.test(routeId) ? Number(routeId) : null;
  const drawerOpen = isNew || editId != null;

  const law = useSelector((state) => state.user.session?.law);
  const canView = getLawFlag(law, 5) === 1;
  const canAdd = getLawFlag(law, 30) === 1;
  const canDelete = getLawFlag(law, 31) === 1;
  const canEditLaw = getLawFlag(law, 32) === 1;
  const canUncheckInLaw = getLawFlag(law, 85) === 1;
  const canUncheckOutLaw = getLawFlag(law, 86) === 1;

  const [filters, setFilters] = useState(() => {
    const fromUrl = filtersFromSearchParams(
      typeof window !== "undefined" ? window.location.search : ""
    );
    return fromUrl || loadStoredFilters();
  });
  const [debouncedQ, setDebouncedQ] = useState(() => (filters.q || "").trim());
  const [branches, setBranches] = useState([]);
  const [vidPutevki, setVidPutevki] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [hasMore, setHasMore] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [cardReloadToken, setCardReloadToken] = useState(0);
  const [bookOpen, setBookOpen] = useState(false);
  const [sellOpen, setSellOpen] = useState(false);
  const [changeNumberOpen, setChangeNumberOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [lifecycle, setLifecycle] = useState(null);
  const [recalcAllowed, setRecalcAllowed] = useState(true);
  const [recalcBlockReason, setRecalcBlockReason] = useState("");
  const requestIdRef = useRef(0);
  const skipRef = useRef(0);
  const hasMoreRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const listBodyRef = useRef(null);
  const loadPageRef = useRef(null);
  const skipUrlSyncRef = useRef(false);

  useEffect(() => {
    saveFilters(filters);
  }, [filters]);

  useEffect(() => {
    const next = filtersToSearchParams(filters);
    const nextStr = next.toString();
    if (searchParams.toString() === nextStr) return undefined;
    const delay = filters.q !== debouncedQ ? SEARCH_DEBOUNCE_MS : 0;
    const t = setTimeout(() => {
      skipUrlSyncRef.current = true;
      setSearchParams(next, { replace: true });
    }, delay);
    return () => clearTimeout(t);
  }, [filters, debouncedQ, searchParams, setSearchParams]);

  useEffect(() => {
    if (skipUrlSyncRef.current) {
      skipUrlSyncRef.current = false;
      return;
    }
    const fromUrl = filtersFromSearchParams(searchParams);
    if (!fromUrl) return;
    setFilters((prev) => (filtersSearchEquals(fromUrl, prev) ? prev : fromUrl));
    setDebouncedQ((fromUrl.q || "").trim());
  }, [searchParams]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ((filters.q || "").trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [filters.q]);

  useEffect(() => {
    if (!canView) return undefined;
    let cancelled = false;
    fetchDocExpLookups()
      .then((data) => {
        if (cancelled) return;
        const lookups = normalizeLookups(data);
        setBranches(lookups.branches || []);
        setVidPutevki(lookups.vidPutevki || []);
      })
      .catch(() => {
        if (cancelled) return;
        setBranches([]);
        setVidPutevki([]);
      });
    return () => {
      cancelled = true;
    };
  }, [canView]);

  const loadPage = useCallback(
    ({ reset }) => {
      if (!reset && (loadingMoreRef.current || !hasMoreRef.current)) return;

      const requestId = ++requestIdRef.current;
      const skip = reset ? 0 : skipRef.current;

      if (reset) {
        setLoading(true);
        setError("");
        const tableBody = listBodyRef.current?.querySelector(".ant-table-body");
        if (tableBody) tableBody.scrollTop = 0;
      } else {
        loadingMoreRef.current = true;
        setLoadingMore(true);
      }

      fetchDocExpList({
        dateMode: filters.dateMode,
        dateBeg: filters.dateBeg,
        dateEnd: filters.dateEnd,
        hideCancelled: filters.idStatus !== STATUS.CANCELLED,
        idStatus: filters.idStatus == null ? undefined : filters.idStatus,
        vidPutevki: filters.vidPutevki || undefined,
        pay: filters.pay || undefined,
        idBranch: filters.idBranch || undefined,
        q: debouncedQ || undefined,
        sortBy: filters.sortBy || "docdate",
        sortDir: filters.sortDir || "asc",
        skip,
        take: PAGE_SIZE
      })
        .then((data) => {
          if (requestId !== requestIdRef.current) return;
          const page = normalizePage(data);
          setRows((prev) => (reset ? page.items : [...prev, ...page.items]));
          skipRef.current = skip + page.items.length;
          hasMoreRef.current = page.hasMore;
          setHasMore(page.hasMore);
        })
        .catch((e) => {
          if (requestId !== requestIdRef.current) return;
          if (reset) {
            setError(e.message || "Не удалось загрузить журнал");
            setRows([]);
            skipRef.current = 0;
            hasMoreRef.current = false;
            setHasMore(false);
          } else {
            message.error(e.message || "Не удалось подгрузить записи");
          }
        })
        .finally(() => {
          if (requestId !== requestIdRef.current) return;
          if (reset) setLoading(false);
          loadingMoreRef.current = false;
          setLoadingMore(false);
        });
    },
    [
      filters.dateMode,
      filters.dateBeg,
      filters.dateEnd,
      filters.idStatus,
      filters.vidPutevki,
      filters.pay,
      filters.idBranch,
      filters.sortBy,
      filters.sortDir,
      debouncedQ
    ]
  );

  useEffect(() => {
    if (!canView) return;
    skipRef.current = 0;
    hasMoreRef.current = false;
    loadPage({ reset: true });
  }, [
    canView,
    filters.dateMode,
    filters.dateBeg,
    filters.dateEnd,
    filters.idStatus,
    filters.vidPutevki,
    filters.pay,
    filters.idBranch,
    filters.sortBy,
    filters.sortDir,
    debouncedQ,
    loadPage
  ]);

  useEffect(() => {
    if (editId != null) setSelectedId(editId);
  }, [editId]);

  loadPageRef.current = loadPage;

  useEffect(() => {
    const body = listBodyRef.current;
    if (!body) return undefined;

    const onScroll = () => {
      if (loadingMoreRef.current || !hasMoreRef.current) return;
      if (body.scrollTop + body.clientHeight >= body.scrollHeight - SCROLL_LOAD_GAP) {
        loadPageRef.current?.({ reset: false });
      }
    };

    body.addEventListener("scroll", onScroll);
    return () => body.removeEventListener("scroll", onScroll);
  }, [rows.length, loading]);

  const reload = useCallback(() => {
    skipRef.current = 0;
    hasMoreRef.current = false;
    loadPage({ reset: true });
  }, [loadPage]);

  const afterBronOp = useCallback(() => {
    reload();
    setCardReloadToken((t) => t + 1);
  }, [reload]);

  const selectedRow = useMemo(
    () => rows.find((r) => r.idDoc === selectedId) || null,
    [rows, selectedId]
  );

  const selectedTargetIds = useMemo(
    () => (selectedIds.length ? selectedIds : selectedId != null ? [selectedId] : []),
    [selectedIds, selectedId]
  );

  const selectedRows = useMemo(
    () => selectedTargetIds.map((id) => rows.find((r) => r.idDoc === id)).filter(Boolean),
    [selectedTargetIds, rows]
  );

  const status = selectedRow?.idStatus ?? null;
  const closed = Boolean(selectedRow?.isClosed);
  const canConfirm = canEditLaw && selectedRow && !closed && status === STATUS.NEW;
  const canUnconfirm =
    canEditLaw &&
    selectedRow &&
    !closed &&
    status === STATUS.CONFIRMED &&
    !selectedRow.sessionId;
  const canCheckIn = canEditLaw && selectedRows.some(isCheckInEligible);
  const canUncheckIn =
    canUncheckInLaw && selectedRow && !closed && status === STATUS.CHECKED_IN;
  const canCheckOut = canEditLaw && selectedRows.some(isCheckOutEligible);
  const canUncheckOut =
    canUncheckOutLaw && selectedRow && !closed && status === STATUS.CHECKED_OUT;
  const moneyClearForCancel =
    Math.abs(Number(selectedRow?.symaPlat) || 0) < 0.005 &&
    Math.abs(Number(selectedRow?.avansOpen) || 0) < 0.005;
  const canCancelDoc =
    canDelete &&
    selectedRow &&
    !closed &&
    status != null &&
    status !== STATUS.CANCELLED &&
    status < STATUS.CHECKED_IN &&
    moneyClearForCancel;
  const canDeleteRow =
    canDelete &&
    selectedRow &&
    !closed &&
    status != null &&
    status < STATUS.CHECKED_IN &&
    !selectedRow.dateBeg;
  const canBook =
    canEditLaw &&
    selectedRow &&
    !closed &&
    status != null &&
    status >= STATUS.NEW &&
    status < STATUS.CHECKED_IN;
  const canClearPlacement =
    canEditLaw &&
    selectedRow &&
    !closed &&
    selectedRow.dateBeg &&
    status != null &&
    status < STATUS.CHECKED_IN;
  const canRecalculate =
    canEditLaw &&
    selectedRow &&
    !closed &&
    status !== STATUS.CANCELLED &&
    recalcAllowed;

  useEffect(() => {
    const id = selectedRow?.idDoc;
    if (id == null) {
      setRecalcAllowed(true);
      setRecalcBlockReason("");
      return undefined;
    }
    let cancelled = false;
    setRecalcAllowed(false);
    fetchDocExpRecalculateCheck(id)
      .then((data) => {
        if (cancelled) return;
        const allowed = Boolean(data?.allowed ?? data?.Allowed);
        setRecalcAllowed(allowed);
        setRecalcBlockReason(
          allowed ? "" : data?.reason || data?.Reason || "Нельзя пересчитать размещение"
        );
      })
      .catch(() => {
        if (cancelled) return;
        setRecalcAllowed(false);
        setRecalcBlockReason("Не удалось проверить возможность пересчёта");
      });
    return () => {
      cancelled = true;
    };
  }, [selectedRow?.idDoc, cardReloadToken]);
  const canChangeNumber =
    canEditLaw &&
    selectedRow &&
    !closed &&
    selectedRow.dateBeg &&
    status != null &&
    status < STATUS.CHECKED_IN;
  const canMovePlacement =
    canEditLaw && selectedRow && !closed && status === STATUS.CHECKED_IN;

  const listSearch = filtersSearchString(filters);

  const openEdit = useCallback(
    (record) => {
      if (record?.idDoc == null) return;
      setSelectedId(record.idDoc);
      setSelectedIds((prev) => (prev.includes(record.idDoc) ? prev : [record.idDoc]));
      navigate({ pathname: `/doc/exp/${record.idDoc}`, search: listSearch });
    },
    [navigate, listSearch]
  );

  const openCreate = useCallback(() => {
    if (!canAdd) return;
    setSellOpen(true);
  }, [canAdd]);

  const closeDrawer = useCallback(() => {
    navigate({ pathname: "/doc/exp", search: listSearch });
  }, [navigate, listSearch]);

  const onSaved = useCallback(() => {
    reload();
  }, [reload]);

  const reportBatch = useCallback(
    (verb, data) => {
      const ok = toNumber(pick(data, "ok", "Ok")) || 0;
      const errors = pick(data, "errors", "Errors") || [];
      const list = Array.isArray(errors) ? errors : [];
      const msgs = list.map((e) => {
        const id = pick(e, "idDoc", "IdDoc");
        const msg = pick(e, "message", "Message") || "ошибка";
        return `№${id}: ${msg}`;
      });
      if (ok && !msgs.length) message.success(`${verb}: ${ok}`);
      else if (ok && msgs.length) message.warning(`${verb}: ${ok}. ${msgs.join("; ")}`);
      else message.error(msgs.join("; ") || "Не выполнено");
      setCardReloadToken((t) => t + 1);
      reload();
    },
    [reload]
  );

  const onCheckIn = useCallback(() => {
    const ids = selectedIds.length ? selectedIds : selectedId != null ? [selectedId] : [];
    if (!ids.length) return;
    if (ids.length > 1) {
      Modal.confirm({
        title: "Выделено несколько путёвок. Заселить плановыми датой/временем заселения?",
        okText: "Да",
        cancelText: "Отмена",
        okButtonProps: { type: "primary" },
        onOk: () =>
          docExpCheckInMany(ids)
            .then((data) => reportBatch("Заселено", data))
            .catch((e) => {
              message.error(e.message || "Ошибка заселения");
            })
      });
      return;
    }
    const row = selectedRows.find(isCheckInEligible) || selectedRow;
    if (!row) return;
    setLifecycle({ mode: "check-in", row });
  }, [selectedIds, selectedId, selectedRows, selectedRow, reportBatch]);

  const onCheckOut = useCallback(() => {
    const ids = selectedIds.length ? selectedIds : selectedId != null ? [selectedId] : [];
    if (!ids.length) return;
    if (ids.length > 1) {
      Modal.confirm({
        title: "Выделено несколько путёвок. Выселить плановыми датой/временем выезда?",
        okText: "Да",
        cancelText: "Отмена",
        okButtonProps: { type: "primary" },
        onOk: () =>
          docExpCheckOutMany(ids)
            .then((data) => reportBatch("Выселено", data))
            .catch((e) => {
              message.error(e.message || "Ошибка выселения");
            })
      });
      return;
    }
    const row = selectedRows.find(isCheckOutEligible) || selectedRow;
    if (!row) return;
    setLifecycle({ mode: "check-out", row });
  }, [selectedIds, selectedId, selectedRows, selectedRow, reportBatch]);

  const onLifecycleOk = useCallback(
    async (dateFact) => {
      if (!lifecycle?.row?.idDoc) return;
      const isIn = lifecycle.mode === "check-in";
      try {
        const data = isIn
          ? await docExpCheckInMany([lifecycle.row.idDoc], dateFact)
          : await docExpCheckOutMany([lifecycle.row.idDoc], dateFact);
        setLifecycle(null);
        reportBatch(isIn ? "Заселено" : "Выселено", data);
      } catch (e) {
        message.error(e.message || "Ошибка операции");
      }
    },
    [lifecycle, reportBatch]
  );

  const runOp = useCallback(
    (title, action) => {
      if (!selectedRow?.idDoc) return;
      Modal.confirm({
        title,
        content: `Путёвка №${selectedRow.docnum || selectedRow.idDoc}`,
        okText: "Да",
        cancelText: "Отмена",
        okButtonProps: { type: "primary" },
        onOk: () =>
          action(selectedRow.idDoc)
            .then(() => {
              message.success("Готово");
              reload();
            })
            .catch((e) => {
              message.error(e.message || "Ошибка операции");
            })
      });
    },
    [selectedRow, reload]
  );

  const onDelete = useCallback(() => {
    if (!canDeleteRow || !selectedRow?.idDoc) return;
    Modal.confirm({
      title: "Удалить путёвку?",
      content: `№${selectedRow.docnum || selectedRow.idDoc}`,
      okText: "Удалить",
      cancelText: "Отмена",
      okButtonProps: { danger: true, type: "primary" },
      onOk: () =>
        deleteDocExp(selectedRow.idDoc)
          .then(() => {
            message.success("Удалено");
            if (editId === selectedRow.idDoc) {
              navigate({ pathname: "/doc/exp", search: listSearch });
            }
            setSelectedId(null);
            setSelectedIds((prev) => prev.filter((id) => id !== selectedRow.idDoc));
            reload();
          })
          .catch((e) => {
            message.error(e.message || "Не удалось удалить");
          })
    });
  }, [canDeleteRow, selectedRow, editId, navigate, reload, listSearch]);

  const toggleCardCheck = useCallback((idDoc, checked) => {
    const id = Number(idDoc);
    if (!Number.isFinite(id)) return;
    setSelectedIds((prev) => {
      const next = checked
        ? prev.includes(id)
          ? prev
          : [...prev, id]
        : prev.filter((x) => x !== id);
      setSelectedId((cur) => {
        if (checked) return id;
        if (cur !== id) return cur;
        return next[0] ?? null;
      });
      return next;
    });
  }, []);

  if (!canView) {
    return (
      <div className="doc-exps">
        <Alert type="warning" showIcon message="Нет права просмотра журнала путёвок" />
      </div>
    );
  }

  return (
    <div className="doc-exps">
      <DocExpsFilters
        value={filters}
        branches={branches}
        vidPutevki={vidPutevki}
        loading={loading}
        canAdd={canAdd}
        canDelete={canDeleteRow}
        canConfirm={canConfirm}
        canUnconfirm={canUnconfirm}
        canCheckIn={canCheckIn}
        canUncheckIn={canUncheckIn}
        canCheckOut={canCheckOut}
        canUncheckOut={canUncheckOut}
        canCancel={canCancelDoc}
        onChange={setFilters}
        onRefresh={reload}
        onCreate={openCreate}
        onDelete={onDelete}
        onConfirm={() => runOp("Подтвердить путёвку?", docExpConfirm)}
        onUnconfirm={() => runOp("Снять подтверждение?", docExpUnconfirm)}
        onCheckIn={onCheckIn}
        onUncheckIn={() => runOp("Отменить заселение?", docExpUncheckIn)}
        onCheckOut={onCheckOut}
        onUncheckOut={() => runOp("Отменить выселение?", docExpUncheckOut)}
        onCancelDoc={() => runOp("Отменить путёвку?", docExpCancel)}
        canBook={canBook}
        canClearPlacement={canClearPlacement}
        canRecalculate={canRecalculate}
        canChangeNumber={canChangeNumber}
        canMovePlacement={canMovePlacement}
        recalculateBlockReason={recalcBlockReason}
        reportIds={
          selectedIds.length
            ? selectedIds
            : selectedId != null
              ? [selectedId]
              : []
        }
        onBook={() => canBook && setBookOpen(true)}
        onClearPlacement={() =>
          runOp("Очистить размещение?", async (id) => {
            const r = await docExpClearPlacement(id);
            setCardReloadToken((t) => t + 1);
            return r;
          })
        }
        onRecalculate={() =>
          runOp("Пересчитать размещение?", async (id) => {
            const r = await docExpRecalculate(id);
            setCardReloadToken((t) => t + 1);
            return r;
          })
        }
        onChangeNumber={() => canChangeNumber && setChangeNumberOpen(true)}
        onMovePlacement={() => canMovePlacement && setMoveOpen(true)}
      />

      {error ? <Alert className="doc-exps__error" type="error" showIcon message={error} /> : null}

      <div className="doc-exps__body" ref={listBodyRef}>
        {loading && rows.length === 0 ? (
          <div className="doc-exps__center">
            <Spin />
          </div>
        ) : rows.length === 0 ? (
          <div className="doc-exps__center doc-exps__empty">Нет путёвок</div>
        ) : (
          <div className="doc-exps__cards">
            {rows.map((record) => (
              <DocExpsRowCard
                key={record.idDoc}
                record={record}
                selected={record.idDoc === selectedId}
                editing={record.idDoc === editId}
                checked={selectedIds.includes(record.idDoc)}
                onCheck={(checked) => toggleCardCheck(record.idDoc, checked)}
                onOpen={() => {
                  setSelectedId(record.idDoc);
                  openEdit(record);
                }}
              />
            ))}
            {loadingMore ? (
              <div className="doc-exps__cards-more">
                <Spin size="small" />
              </div>
            ) : null}
          </div>
        )}
      </div>

      <DocExp
        open={drawerOpen}
        idDoc={editId}
        isNew={isNew}
        canEdit={isNew ? canAdd : canEditLaw}
        reloadToken={cardReloadToken}
        onClose={closeDrawer}
        onSaved={onSaved}
      />

      <DocExpLifecycleModal
        open={Boolean(lifecycle)}
        mode={lifecycle?.mode}
        row={lifecycle?.row}
        onCancel={() => setLifecycle(null)}
        onOk={onLifecycleOk}
      />
      <DocExpBookModal
        open={bookOpen}
        idDoc={selectedRow?.idDoc}
        initial={selectedRow}
        onCancel={() => setBookOpen(false)}
        onDone={() => {
          setBookOpen(false);
          afterBronOp();
        }}
      />
      <DocExpSellBookModal
        open={sellOpen}
        asDrawer
        mode="category"
        onCancel={() => setSellOpen(false)}
        onDone={(saved) => {
          setSellOpen(false);
          afterBronOp();
          const newId = toNumber(saved?.idDoc ?? saved?.IdDoc);
          if (newId != null) navigate({ pathname: `/doc/exp/${newId}`, search: listSearch });
        }}
      />
      <DocExpChangeNumberModal
        open={changeNumberOpen}
        idDoc={selectedRow?.idDoc}
        row={selectedRow}
        onCancel={() => setChangeNumberOpen(false)}
        onDone={() => {
          setChangeNumberOpen(false);
          afterBronOp();
        }}
      />
      <DocExpMoveModal
        open={moveOpen}
        idDoc={selectedRow?.idDoc}
        row={selectedRow}
        onCancel={() => setMoveOpen(false)}
        onDone={() => {
          setMoveOpen(false);
          afterBronOp();
        }}
      />
    </div>
  );
};

export default DocExps;
